import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Select,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const getTagValues = (campaignAppData, key) =>
  campaignAppData?.tags?.find((tag) => tag.key === key)?.values;

const buildAllModelsSelection = (mmyForPortfolio) => {
  const allModelsSelected = {};
  mmyForPortfolio?.[0]?.models?.forEach((modelItem) => {
    allModelsSelected[modelItem.name] = {
      selected: true,
      years: modelItem.years.map((year) => year.toString()),
    };
  });
  return allModelsSelected;
};

const buildSelectedModelsFromInclusions = (mmyInclusions, portfolioId, mmyForPortfolio) => {
  const modelsSelected = {};
  const mappedPortfolioId = portfolioId === "TML" ? "TATA" : portfolioId;

  mmyInclusions.forEach((inclusion) => {
    const parts = inclusion.split("_");
    if (parts.length !== 3) {
      return;
    }

    const [first, modelName, year] = parts;
    const isValidInclusion =
      first === mappedPortfolioId || mmyForPortfolio?.[0]?.make === first;

    if (!isValidInclusion) {
      return;
    }

    if (!modelsSelected[modelName]) {
      modelsSelected[modelName] = {
        selected: false,
        years: [],
      };
    }

    if (year === "ALL") {
      modelsSelected[modelName].selected = true;
      const modelData = mmyForPortfolio?.[0]?.models.find(
        (modelItem) => modelItem.name === modelName
      );
      if (modelData) {
        modelsSelected[modelName].years = modelData.years.map((y) =>
          y.toString()
        );
      }
      return;
    }

    modelsSelected[modelName].years.push(year);
    if (modelsSelected[modelName].years.length > 0) {
      modelsSelected[modelName].selected = true;
    }
  });

  return modelsSelected;
};

export default function DeleteCampaignDialog({ open, onClose, onDelete, CampaignAppData }) {
  const [softwareVersionDialogOpen, setSoftwareVersionDialogOpen] =
      useState(false);
    const [selectedSoftwareVersions, setSelectedSoftwareVersions] = useState([]);
    const [isAllSoftwareSelected, setIsAllSoftwareSelected] = useState(false);
    const [countriesDialogOpen, setCountriesDialogOpen] = useState(false);
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [isAllCountriesSelected, setIsAllCountriesSelected] = useState(false);
      const [modelYearDialogOpen, setModelYearDialogOpen] = useState(false);
      const [selectedModels, setSelectedModels] = useState({});
      const [isAllSelected, setIsAllSelected] = useState(false);
    const clusterNumberData = useSelector(
        (state) => state.clusterNumberReducer.clusterNumberData
      );
      const MMYforPortfolio = useSelector(
        (state) => state.makeModelYearReducer.mmyData
      );

        const countries = useSelector((state) => state.oemAdminCountires.countries);
    
      useEffect(() => {
        const mmyInclusions = getTagValues(CampaignAppData, "mmy_inclusion");
        if (!mmyInclusions) {
          return;
        }

        const portfolioId = sessionStorage.getItem("portfolioId") || "";
        const mappedPortfolioId = portfolioId === "TML" ? "TATA" : portfolioId;
        const allSelectionKey = `${mappedPortfolioId}_ALL_ALL`;
        const hasAllSelected = mmyInclusions.includes(allSelectionKey);

        setIsAllSelected(hasAllSelected);
        if (hasAllSelected) {
          setSelectedModels(buildAllModelsSelection(MMYforPortfolio));
          return;
        }

        setSelectedModels(
          buildSelectedModelsFromInclusions(
            mmyInclusions,
            portfolioId,
            MMYforPortfolio
          )
        );
      }, [CampaignAppData, MMYforPortfolio]);
    
      // Add useEffect to parse software version selections
      useEffect(() => {
        const clusterValues = getTagValues(CampaignAppData, "clusterNumber");
        if (!clusterValues) {
          return;
        }

        const hasAllSelected = clusterValues.includes("All");
        setIsAllSoftwareSelected(hasAllSelected);

        if (hasAllSelected) {
          setSelectedSoftwareVersions(clusterNumberData || []);
          return;
        }

        setSelectedSoftwareVersions(clusterValues);
      }, [CampaignAppData, clusterNumberData]);

       const getCountriesDisplayText = () => {
    if (isAllCountriesSelected) {
      return "All Countries";
    }
    const selectedCount = selectedCountries.length;
    if (selectedCount === 0) return "Select Countries";
    return `${selectedCount} Countr${selectedCount > 1 ? "ies" : "y"} Selected`;
  };

   const handleCountriesClick = () => {
    setCountriesDialogOpen(true);
  };

  // Inline helper component for consistent dialog list rendering
  const renderDialogList = (title, items, renderItem, emptyMessage) => (
    <Box>
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "16px",
          mb: 2,
          color: "#2A2C2D",
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          maxHeight: 400,
          overflowY: "auto",
          border: "1px solid #E0E0E0",
          borderRadius: "8px",
          background: "#fafafa",
        }}
      >
        <List dense>
          {items && items.length > 0 ? (
            items.map(renderItem)
          ) : (
            <ListItem>
              <ListItemText
                primary={emptyMessage}
                sx={{ textAlign: "center", color: "#888" }}
              />
            </ListItem>
          )}
        </List>
      </Box>
    </Box>
  );

    const getModelYearDisplayText = () => {
    if (isAllSelected) {
      return "All Models & Years";
    }
    const selectedCount = Object.keys(selectedModels).length;
    if (selectedCount === 0) return "Select Model Year";
    return `${selectedCount} Model(s) Selected`;
  };

   const handleModelYearClick = () => {
    setModelYearDialogOpen(true);
  };

   const getSoftwareVersionDisplayText = () => {
    if (isAllSoftwareSelected) {
      return "All Software Versions";
    }
    const selectedCount = selectedSoftwareVersions.length;
    if (selectedCount === 0) return "Select Software Version";
    return `${selectedCount} Version(s) Selected`;
  };

   const handleSoftwareVersionClick = () => {
    setSoftwareVersionDialogOpen(true);
  };

   const handleModelYearDialogClose = () => {
    setModelYearDialogOpen(false);
  };

  const handleSoftwareVersionDialogClose = () => {
    setSoftwareVersionDialogOpen(false);
  };

   const handleCountriesDialogClose = () => {
    setCountriesDialogOpen(false);
   }

  // Helper function to render a selection box
  const renderSelectionBox = (label, displayText, onClick, show = true) => {
    if (!show) return null;
    
    return (
      <Box sx={{ minWidth: 150 }}>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: "14px",
            color: "#5A5B5C",
            mb: 0.5,
          }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "16px",
              color: "#2A2C2D",
              mb: 0.5,
            }}
          >
            {label}
          </Typography>
          <Select
            value={displayText}
            size="small"
            displayEmpty
            sx={{
              fontSize: "14px",
              "& .MuiSelect-select": {
                py: 0.5,
                cursor: "pointer",
              },
            }}
            onOpen={(e) => {
              e.preventDefault();
              onClick();
            }}
            open={false}
          >
            {/* Empty - no menu items needed */}
          </Select>
        </Box>
      </Box>
    );
  };

  // Helper function to render list items with checkboxes
  const renderListItem = (key, label) => (
    <ListItem key={key} sx={{ py: 1 }}>
      <ListItemIcon sx={{ minWidth: 32 }}>
        <Checkbox
          edge="start"
          checked={true}
          disabled={true}
          size="small"
        />
      </ListItemIcon>
      <ListItemText
        primary={label}
        sx={{
          "& .MuiListItemText-primary": {
            fontSize: "14px",
            fontWeight: 500,
          },
        }}
      />
    </ListItem>
  );

  // Helper function to render dialog structure
  const renderDialog = (open, onClose, title, children) => (
    open && (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 600,
            fontSize: "18px",
            pb: 1,
          }}
        >
          {title}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {children}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "flex-end", pb: 2, px: 3 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: 14,
              background: "#f5f5f5",
              color: "#222",
              borderRadius: "6px",
              textTransform: "none",
              px: 4,
              border: "none",
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    )
  );
    
  useEffect(() => {
    const countryValues = getTagValues(CampaignAppData, "country");
    if (countryValues) {

        // Check if "All" is present
        const hasAllSelected = countryValues.includes("All");
        setIsAllCountriesSelected(hasAllSelected);

        if (hasAllSelected) {
          // Select all countries
          setSelectedCountries(countries || []);
        } else {
          // Map ISO3 codes to full country objects
          const selectedCountryObjs = countryValues
            .map((iso3Code) => countries?.find((c) => c.iso3Code === iso3Code))
            .filter(Boolean); // Remove undefined values
          setSelectedCountries(selectedCountryObjs);
        }
    }
  }, [CampaignAppData, countries]);
  

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 600,
          fontSize: "22px",
          pb: 0,
        }}
      >
        Delete Campaign
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Paper
          variant="outlined"
          sx={{
            display: "flex",
            gap: 2,
            p: 2,
            mb: 2,
            background: "#fafafa",
            borderRadius: "8px",
            boxShadow: "none",
          }}
        >
          {renderSelectionBox("Countries", getCountriesDisplayText(), handleCountriesClick)}
          {renderSelectionBox("Model Year", getModelYearDisplayText(), handleModelYearClick)}
          {sessionStorage.getItem("idpLogin") === "true" && renderSelectionBox("Software Version", getSoftwareVersionDisplayText(), handleSoftwareVersionClick)}
        </Paper>
        <Typography sx={{ mt: 1, mb: 2, fontSize: "16px", color: "#222" }}>
          Are you sure you wish to permanently remove this Campaign?<br />
          <span style={{ color: "#717273" }}>This action cannot be reversed.</span>
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "flex-end", pb: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            fontWeight: 500,
            fontSize: "16px",
            fontFamily: "Roboto",
            background: "#f5f5f5",
            color: "#222",
            borderRadius: "6px",
            px: 4,
            mr: 1,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            // Call your delete logic here
            onDelete();
            onClose();
          }}
          variant="outlined"
          sx={{
            ml: 2,
            border: "1px solid #E53935",
            color: "#E53935",
            fontWeight: 500,
            fontSize: "16px",
            fontFamily: "Roboto",
            borderRadius: "6px",
            px: 4,
            background: "#fff",
            "&:hover": {
              background: "#ffeaea",
              borderColor: "#E53935",
            },
          }}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>

     { modelYearDialogOpen && renderDialog(
        modelYearDialogOpen,
        handleModelYearDialogClose,
        "Model Year Selection",
        <Box sx={{ display: "flex", gap: 2 }}>
          {/* Model Column */}
          <Box sx={{ flex: 1, borderRight: "1px solid #E0E0E0", pr: 2 }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "16px",
                mb: 2,
                color: "#2A2C2D",
              }}
            >
              Model
            </Typography>
            <Box
              sx={{
                maxHeight: 400,
                overflowY: "auto",
                border: "1px solid #E0E0E0",
                borderRadius: "8px",
                background: "#fafafa",
              }}
            >
              <List dense>
                {/* Show models that have selections or if All is selected */}
                {MMYforPortfolio?.[0]?.models.map((model) => {
                  console.log("Rendering model:", model);
                  const hasSelection =
                    selectedModels[model.name]?.selected ||
                    selectedModels[model.name]?.years?.length > 0;
                  console.log(
                    "Model has selection:",
                    hasSelection,
                    "Is All Selected:",
                    isAllSelected
                  );
                  // Only show models that are selected or if "All" is selected
                  if (!hasSelection && !isAllSelected) {
                    return null;
                  }

                  return renderListItem(model.name, model.name);
                })}
              </List>
            </Box>
          </Box>

          {/* Year Column - Show only selected models and their selected years */}
          <Box sx={{ flex: 1, pl: 2 }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "16px",
                mb: 2,
                color: "#2A2C2D",
              }}
            >
              Year
            </Typography>
            <Box sx={{ mb: 2, height: 40 }} /> {/* Spacer for alignment */}
            <Box
              sx={{
                maxHeight: 400,
                overflowY: "auto",
                border: "1px solid #E0E0E0",
                borderRadius: "8px",
                background: "#fafafa",
                mt:"-55px !important"
              }}
            >
              {/* Show models that have selections */}
              {MMYforPortfolio?.[0]?.models.map((model) => {
                const selectedYears =
                  selectedModels[model.name]?.years || [];

                // Only show years section if this model has selected years OR if "All" is selected
                if (selectedYears.length === 0 && !isAllSelected) {
                  return null;
                }

                return (
                  <Box key={model.name} sx={{ mb: 2 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "14px",
                        color: "#0070f3",
                        px: 2,
                        py: 1,
                        background: "#f5f5f5",
                      }}
                    >
                      {model.name}
                    </Typography>
                    <List dense>
                      {/* Show only selected years */}
                      {model.years.map((year) => {
                        console.log(
                          "Checking year:",
                          year,
                          "for model:",
                          model.name
                        );
                        const isYearSelected =
                          selectedYears.includes(year.toString()) ||
                          isAllSelected;

                        // Only render if year is selected
                        if (!isYearSelected) return null;

                        return renderListItem(`${model.name}-${year}`, year);
                      })}
                    </List>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}
    
      {softwareVersionDialogOpen && renderDialog(
        softwareVersionDialogOpen,
        handleSoftwareVersionDialogClose,
        "Software Version Selection",
        renderDialogList(
          "Software Versions",
          isAllSoftwareSelected
            ? clusterNumberData
            : selectedSoftwareVersions,
          (version) => renderListItem(version, version),
          isAllSoftwareSelected
            ? "No software versions available"
            : "No software versions selected"
        )
      )}
    
      {countriesDialogOpen && renderDialog(
        countriesDialogOpen,
        handleCountriesDialogClose,
        "Countries Selection",
        renderDialogList(
          "Countries",
          (isAllCountriesSelected ? countries : selectedCountries)?.sort((a, b) =>
            a.name.localeCompare(b.name)
          ),
          (country) => renderListItem(country.iso3Code, country.name),
          isAllCountriesSelected
            ? "No countries available"
            : "No countries selected"
        )
      )}
        </>
  );
}

DeleteCampaignDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onDelete: PropTypes.func,
  CampaignAppData: PropTypes.shape({
    tags: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        values: PropTypes.arrayOf(PropTypes.string),
      })
    ),
  }),
};